/**
 * Hooks Test Page
 * Tests useFundingRate and enhanced usePositions hooks:
 * - useFundingRate: HTTP initialization + WebSocket real-time updates
 * - usePositions: WebSocket primary source with HTTP fallback
 */

import { useState } from "react";
import { useFundingRate } from "../hooks/useFundingRate";
import { usePositions } from "../hooks/usePositions";
import {
  DEFAULT_BASE_URL,
  DEFAULT_WS_URL,
  DEFAULT_DEVICE_ID,
} from "../configs/base";

export default function HooksTestPage() {
  const [baseURL, setBaseURL] = useState(DEFAULT_BASE_URL);
  const [wsURL, setWsURL] = useState(DEFAULT_WS_URL);
  const [authToken, setAuthToken] = useState(
    localStorage.getItem("authToken") || "",
  );
  const [walletAddress, setWalletAddress] = useState("");
  const [deviceID, setDeviceID] = useState(DEFAULT_DEVICE_ID);

  // Funding Rate Configuration
  const [fundingRateSymbols, setFundingRateSymbols] =
    useState<string>("BTC/USDT");
  const [fundingRateHistoryDepth, setFundingRateHistoryDepth] =
    useState<number>(1);

  // Positions Configuration
  const [useWebSocketForPositions, setUseWebSocketForPositions] =
    useState(true);
  const [positionsRefreshInterval, setPositionsRefreshInterval] =
    useState<number>(5000);

  // Parse symbols from comma-separated string
  const fundingRateSymbolsArray = fundingRateSymbols
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  // Funding Rate Hook
  const {
    fundingRates,
    getFundingRate,
    getLatestFundingRate,
    loading: fundingRateLoading,
    error: fundingRateError,
    isConnected: fundingRateConnected,
    refresh: refreshFundingRate,
  } = useFundingRate({
    baseURL,
    wsURL,
    symbols: fundingRateSymbolsArray,
    historyDepth: fundingRateHistoryDepth,
    authToken,
    walletAddress,
    deviceID,
  });

  // Positions Hook
  const {
    positions,
    loading: positionsLoading,
    error: positionsError,
    isConnected: positionsConnected,
    refresh: refreshPositions,
  } = usePositions({
    baseURL,
    wsURL: useWebSocketForPositions ? wsURL : undefined,
    authToken,
    walletAddress,
    deviceID,
    refreshInterval: useWebSocketForPositions
      ? undefined
      : positionsRefreshInterval,
    useWebSocket: useWebSocketForPositions,
  });

  return (
    <div className="App-content-row">
      <h1 style={{ marginBottom: "2rem", color: "#333" }}>Hooks Test Page</h1>

      {/* Configuration Section */}
      <div className="App-section">
        <h2 style={{ marginTop: 0, marginBottom: "1rem", color: "#495057" }}>
          Configuration
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "1rem",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "0.5rem",
                fontWeight: "500",
              }}
            >
              API Base URL:
              <input
                type="text"
                value={baseURL}
                onChange={(e) => setBaseURL(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.5rem",
                  marginTop: "0.25rem",
                  borderRadius: "4px",
                  border: "1px solid #ced4da",
                }}
              />
            </label>
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "0.5rem",
                fontWeight: "500",
              }}
            >
              WebSocket URL:
              <input
                type="text"
                value={wsURL}
                onChange={(e) => setWsURL(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.5rem",
                  marginTop: "0.25rem",
                  borderRadius: "4px",
                  border: "1px solid #ced4da",
                }}
              />
            </label>
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "0.5rem",
                fontWeight: "500",
              }}
            >
              Auth Token:
              <input
                type="text"
                value={authToken}
                onChange={(e) => {
                  setAuthToken(e.target.value);
                  if (e.target.value) {
                    localStorage.setItem("authToken", e.target.value);
                  } else {
                    localStorage.removeItem("authToken");
                  }
                }}
                placeholder="Required for positions"
                style={{
                  width: "100%",
                  padding: "0.5rem",
                  marginTop: "0.25rem",
                  borderRadius: "4px",
                  border: "1px solid #ced4da",
                }}
              />
            </label>
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "0.5rem",
                fontWeight: "500",
              }}
            >
              Wallet Address:
              <input
                type="text"
                value={walletAddress}
                onChange={(e) => setWalletAddress(e.target.value)}
                placeholder="Optional"
                style={{
                  width: "100%",
                  padding: "0.5rem",
                  marginTop: "0.25rem",
                  borderRadius: "4px",
                  border: "1px solid #ced4da",
                }}
              />
            </label>
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "0.5rem",
                fontWeight: "500",
              }}
            >
              Device ID:
              <input
                type="text"
                value={deviceID}
                onChange={(e) => setDeviceID(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.5rem",
                  marginTop: "0.25rem",
                  borderRadius: "4px",
                  border: "1px solid #ced4da",
                }}
              />
            </label>
          </div>
        </div>
      </div>

      {/* Funding Rate Section */}
      <div className="App-section">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1rem",
          }}
        >
          <h2 style={{ margin: 0, color: "#495057" }}>Funding Rate Hook</h2>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <span
              style={{
                padding: "0.25rem 0.75rem",
                borderRadius: "4px",
                fontSize: "0.875rem",
                backgroundColor: fundingRateConnected ? "#d4edda" : "#f8d7da",
                color: fundingRateConnected ? "#155724" : "#721c24",
              }}
            >
              {fundingRateConnected ? "WS Connected" : "WS Disconnected"}
            </span>
            <button
              onClick={() => refreshFundingRate()}
              disabled={fundingRateLoading}
              style={{
                padding: "0.5rem 1rem",
                borderRadius: "4px",
                border: "1px solid #007bff",
                backgroundColor: "#007bff",
                color: "white",
                cursor: fundingRateLoading ? "not-allowed" : "pointer",
                opacity: fundingRateLoading ? 0.6 : 1,
              }}
            >
              {fundingRateLoading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "1rem",
            marginBottom: "1rem",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "0.5rem",
                fontWeight: "500",
              }}
            >
              Symbols (comma-separated):
              <input
                type="text"
                value={fundingRateSymbols}
                onChange={(e) => setFundingRateSymbols(e.target.value)}
                placeholder="BTC/USDT, ETH/USDT"
                style={{
                  width: "100%",
                  padding: "0.5rem",
                  marginTop: "0.25rem",
                  borderRadius: "4px",
                  border: "1px solid #ced4da",
                }}
              />
            </label>
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "0.5rem",
                fontWeight: "500",
              }}
            >
              History Depth:
              <input
                type="number"
                value={fundingRateHistoryDepth}
                onChange={(e) =>
                  setFundingRateHistoryDepth(
                    Math.max(1, parseInt(e.target.value) || 1),
                  )
                }
                min="1"
                style={{
                  width: "100%",
                  padding: "0.5rem",
                  marginTop: "0.25rem",
                  borderRadius: "4px",
                  border: "1px solid #ced4da",
                }}
              />
            </label>
          </div>
        </div>

        {fundingRateError && (
          <div
            style={{
              padding: "0.75rem",
              backgroundColor: "#f8d7da",
              color: "#721c24",
              borderRadius: "4px",
              marginBottom: "1rem",
            }}
          >
            Error: {fundingRateError}
          </div>
        )}

        {fundingRateLoading && fundingRates.size === 0 ? (
          <div
            style={{ padding: "1rem", textAlign: "center", color: "#6c757d" }}
          >
            Loading funding rates...
          </div>
        ) : fundingRates.size === 0 ? (
          <div
            style={{ padding: "1rem", textAlign: "center", color: "#6c757d" }}
          >
            No funding rate data available
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))",
              gap: "1rem",
            }}
          >
            {fundingRateSymbolsArray.map((symbol) => {
              const latest = getLatestFundingRate(symbol);
              const data = getFundingRate(symbol);
              const isHistory = Array.isArray(data);

              return (
                <div
                  key={symbol}
                  style={{
                    border: "1px solid #dee2e6",
                    borderRadius: "4px",
                    padding: "1rem",
                    backgroundColor: "#f8f9fa",
                  }}
                >
                  <h3
                    style={{
                      marginTop: 0,
                      marginBottom: "0.75rem",
                      color: "#495057",
                    }}
                  >
                    {symbol}
                    {isHistory && (
                      <span
                        style={{
                          marginLeft: "0.5rem",
                          fontSize: "0.875rem",
                          color: "#6c757d",
                        }}
                      >
                        ({data.length} updates)
                      </span>
                    )}
                  </h3>
                  {latest ? (
                    <div style={{ fontSize: "0.875rem", lineHeight: "1.6" }}>
                      <div>
                        <strong>Funding Rate:</strong>{" "}
                        {parseFloat(latest.funding_rate || "0").toFixed(6)}
                      </div>
                      <div>
                        <strong>Mark Price:</strong>{" "}
                        {latest.mark_price || "N/A"}
                      </div>
                      <div>
                        <strong>Index Price:</strong>{" "}
                        {latest.index_price || "N/A"}
                      </div>
                      <div>
                        <strong>Premium Rate:</strong>{" "}
                        {latest.premium_rate || "N/A"}
                      </div>
                      <div>
                        <strong>EMA Funding Rate:</strong>{" "}
                        {latest.ema_funding_rate || "N/A"}
                      </div>
                      <div>
                        <strong>Next Funding Time:</strong>{" "}
                        {latest.next_funding_time_unix
                          ? new Date(
                              latest.next_funding_time_unix * 1000,
                            ).toLocaleString()
                          : "N/A"}
                      </div>
                      <div>
                        <strong>Funding Interval:</strong>{" "}
                        {latest.funding_interval_seconds
                          ? `${latest.funding_interval_seconds}s`
                          : "N/A"}
                      </div>
                      <div>
                        <strong>Valid:</strong> {latest.is_valid ? "Yes" : "No"}
                      </div>
                      <div>
                        <strong>Stale:</strong> {latest.is_stale ? "Yes" : "No"}
                      </div>
                      {latest.option_type && (
                        <>
                          <div>
                            <strong>Option Type:</strong> {latest.option_type}
                          </div>
                          <div>
                            <strong>Strike Price:</strong>{" "}
                            {latest.strike_price || "N/A"}
                          </div>
                        </>
                      )}
                      <div
                        style={{
                          marginTop: "0.5rem",
                          fontSize: "0.75rem",
                          color: "#6c757d",
                        }}
                      >
                        Updated:{" "}
                        {latest.calculation_time_unix
                          ? new Date(
                              latest.calculation_time_unix * 1000,
                            ).toLocaleString()
                          : "N/A"}
                      </div>
                    </div>
                  ) : (
                    <div style={{ color: "#6c757d" }}>No data available</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Positions Section */}
      <div className="App-section">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1rem",
          }}
        >
          <h2 style={{ margin: 0, color: "#495057" }}>Positions Hook</h2>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <span
              style={{
                padding: "0.25rem 0.75rem",
                borderRadius: "4px",
                fontSize: "0.875rem",
                backgroundColor: positionsConnected ? "#d4edda" : "#f8d7da",
                color: positionsConnected ? "#155724" : "#721c24",
              }}
            >
              {positionsConnected
                ? "WS Connected"
                : useWebSocketForPositions
                  ? "WS Disconnected"
                  : "Polling Mode"}
            </span>
            <button
              onClick={() => refreshPositions()}
              disabled={positionsLoading}
              style={{
                padding: "0.5rem 1rem",
                borderRadius: "4px",
                border: "1px solid #007bff",
                backgroundColor: "#007bff",
                color: "white",
                cursor: positionsLoading ? "not-allowed" : "pointer",
                opacity: positionsLoading ? 0.6 : 1,
              }}
            >
              {positionsLoading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "1rem",
            marginBottom: "1rem",
          }}
        >
          <div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={useWebSocketForPositions}
                onChange={(e) => setUseWebSocketForPositions(e.target.checked)}
              />
              <span style={{ fontWeight: "500" }}>
                Use WebSocket (primary source)
              </span>
            </label>
          </div>
          {!useWebSocketForPositions && (
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "500",
                }}
              >
                Polling Interval (ms):
                <input
                  type="number"
                  value={positionsRefreshInterval}
                  onChange={(e) =>
                    setPositionsRefreshInterval(
                      Math.max(1000, parseInt(e.target.value) || 5000),
                    )
                  }
                  min="1000"
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    marginTop: "0.25rem",
                    borderRadius: "4px",
                    border: "1px solid #ced4da",
                  }}
                />
              </label>
            </div>
          )}
        </div>

        {positionsError && (
          <div
            style={{
              padding: "0.75rem",
              backgroundColor: "#f8d7da",
              color: "#721c24",
              borderRadius: "4px",
              marginBottom: "1rem",
            }}
          >
            Error: {positionsError}
          </div>
        )}

        {positionsLoading && positions.length === 0 ? (
          <div
            style={{ padding: "1rem", textAlign: "center", color: "#6c757d" }}
          >
            Loading positions...
          </div>
        ) : positions.length === 0 ? (
          <div
            style={{ padding: "1rem", textAlign: "center", color: "#6c757d" }}
          >
            No positions found
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))",
              gap: "1rem",
            }}
          >
            {positions.map((position, index) => (
              <div
                key={`${position.marketId}-${index}`}
                style={{
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  padding: "1rem",
                  backgroundColor: "#f8f9fa",
                }}
              >
                <h3
                  style={{
                    marginTop: 0,
                    marginBottom: "0.75rem",
                    color: "#495057",
                  }}
                >
                  {position.symbol || `Market ${position.marketId}`}
                </h3>
                <div style={{ fontSize: "0.875rem", lineHeight: "1.6" }}>
                  <div>
                    <strong>Side:</strong>{" "}
                    <span
                      style={{
                        color: position.side === "LONG" ? "#28a745" : "#dc3545",
                        fontWeight: "600",
                      }}
                    >
                      {position.side}
                    </span>
                  </div>
                  <div>
                    <strong>Size:</strong> {position.size.toFixed(8)}
                  </div>
                  <div>
                    <strong>Entry Price:</strong>{" "}
                    {position.entryPrice.toFixed(8)}
                  </div>
                  <div>
                    <strong>Mark Price:</strong> {position.markPrice.toFixed(8)}
                  </div>
                  {position.liquidationPrice !== undefined && (
                    <div>
                      <strong>Liquidation Price:</strong>{" "}
                      {position.liquidationPrice.toFixed(8)}
                    </div>
                  )}
                  {position.unrealizedPnL !== undefined && (
                    <div>
                      <strong>Unrealized PnL:</strong>{" "}
                      <span
                        style={{
                          color:
                            (position.unrealizedPnL || 0) >= 0
                              ? "#28a745"
                              : "#dc3545",
                        }}
                      >
                        {position.unrealizedPnL.toFixed(8)}
                      </span>
                    </div>
                  )}
                  {position.leverage !== undefined && (
                    <div>
                      <strong>Leverage:</strong> {position.leverage}x
                    </div>
                  )}
                  <div>
                    <strong>Margin:</strong> {position.margin.toFixed(8)}
                  </div>
                  <div>
                    <strong>Margin Mode:</strong> {position.marginMode || "N/A"}
                  </div>
                  <div
                    style={{
                      marginTop: "0.5rem",
                      fontSize: "0.75rem",
                      color: "#6c757d",
                    }}
                  >
                    Market ID: {position.marketId}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
